import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import logoImg from '../assets/logo.png';

interface Section {
  id: string;
  title: string;
  content: React.ReactNode;
}

const sections: Section[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    content: (
      <div className="space-y-4">
        <p>Welcome to the Unified Multi-LLM Platform. This guide will help you get up and running quickly.</p>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Creating Your Account</h4>
        <ol className="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400">
          <li>Visit the <Link to="/" className="text-blue-600 dark:text-blue-400 hover:underline">homepage</Link> and click <strong className="text-gray-900 dark:text-white">Try Now</strong>.</li>
          <li>Enter your full name, email address, and create a strong password.</li>
          <li>Your password must include at least 8 characters, an uppercase letter, a lowercase letter, a number, and a special character.</li>
          <li>Check your email for a verification link and click it to activate your account.</li>
          <li>Sign in with your credentials to access the platform.</li>
        </ol>
        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            <strong>Tip:</strong> If you don't receive the verification email, check your spam folder. You can also request a new verification email from the login page.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'chat-interface',
    title: 'Using the Chat Interface',
    content: (
      <div className="space-y-4">
        <p>The chat interface is your primary workspace for interacting with AI models.</p>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Starting a Conversation</h4>
        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
          <li>After signing in, you'll be taken to the chat page automatically.</li>
          <li>Select an AI model from the model selector dropdown at the top of the chat.</li>
          <li>Type your message in the input box at the bottom and press <strong className="text-gray-900 dark:text-white">Enter</strong> or click the send button.</li>
          <li>Responses stream in real time, so you can see the AI's answer as it's being generated.</li>
        </ul>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mt-6">Conversation Sidebar</h4>
        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
          <li>All your conversations appear in the left sidebar.</li>
          <li>Click any conversation to resume it.</li>
          <li>Use the search bar to find past conversations by content.</li>
          <li>Click the <strong className="text-gray-900 dark:text-white">New Chat</strong> button to start a fresh conversation.</li>
        </ul>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mt-6">Switching Models Mid-Chat</h4>
        <p className="text-gray-600 dark:text-gray-400">
          You can change the AI model at any time during a conversation using the model selector.
          The new model will continue with the full context of your conversation.
        </p>
      </div>
    ),
  },
  {
    id: 'models',
    title: 'Available AI Models',
    content: (
      <div className="space-y-4">
        <p>The platform provides access to models from multiple providers:</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { provider: 'OpenAI', models: 'GPT-4o, GPT-4 Turbo, GPT-3.5 Turbo', color: 'gray' },
            { provider: 'Anthropic', models: 'Claude 3.5 Sonnet, Claude 3 Opus, Claude 3 Haiku', color: 'orange' },
            { provider: 'Google', models: 'Gemini Pro, Gemini Flash', color: 'blue' },
            { provider: 'Mistral', models: 'Mistral Large, Mistral Medium, Mistral Small', color: 'purple' },
            { provider: 'DeepSeek', models: 'DeepSeek Chat, DeepSeek Coder', color: 'cyan' },
            { provider: 'Groq / Llama', models: 'Llama 3.1, Llama 3, Mixtral', color: 'green' },
          ].map((p) => (
            <div key={p.provider} className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <h5 className="font-semibold text-gray-900 dark:text-white mb-1">{p.provider}</h5>
              <p className="text-sm text-gray-600 dark:text-gray-400">{p.models}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-sm text-amber-800 dark:text-amber-300">
            <strong>Note:</strong> Available models depend on your plan and configuration. Some models may be disabled by your administrator.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'model-comparison',
    title: 'Model Comparison',
    content: (
      <div className="space-y-4">
        <p>One of the platform's most powerful features is the ability to compare two models side by side.</p>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">How to Compare Models</h4>
        <ol className="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400">
          <li>In the chat interface, click the <strong className="text-gray-900 dark:text-white">Compare</strong> button (two-panel icon) near the model selector.</li>
          <li>Select two different AI models from the dropdown menus.</li>
          <li>Type your prompt and send it. Both models receive the same message simultaneously.</li>
          <li>View the responses side by side to compare quality, style, and accuracy.</li>
        </ol>
        <p className="text-gray-600 dark:text-gray-400 mt-4">
          This is especially useful for evaluating which model performs best
          for specific types of questions or tasks.
        </p>
      </div>
    ),
  },
  {
    id: 'file-uploads',
    title: 'File Uploads',
    content: (
      <div className="space-y-4">
        <p>Upload documents to provide additional context for your AI conversations.</p>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Supported File Types</h4>
        <p className="text-gray-600 dark:text-gray-400">
          The platform supports common document formats including PDF, TXT, DOCX, CSV, and code files.
          Text is automatically extracted from uploaded files and included in the AI's context.
        </p>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mt-4">How to Upload Files</h4>
        <ol className="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400">
          <li>Click the attachment/paperclip icon in the chat input area.</li>
          <li>Select a file from your device or drag and drop it into the chat.</li>
          <li>The file will be processed and its content will be available to the AI model.</li>
          <li>Ask questions about the file content in your next message.</li>
        </ol>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mt-4">File Library</h4>
        <p className="text-gray-600 dark:text-gray-400">
          Access your uploaded files from the File Library. You can manage your uploads and see your storage quota usage.
        </p>
      </div>
    ),
  },
  {
    id: 'folders',
    title: 'Organizing with Folders',
    content: (
      <div className="space-y-4">
        <p>Keep your conversations organized using folders.</p>
        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
          <li><strong className="text-gray-900 dark:text-white">Create a folder:</strong> Click the folder icon in the sidebar and give it a name.</li>
          <li><strong className="text-gray-900 dark:text-white">Move conversations:</strong> Drag conversations into folders, or use the conversation menu to move them.</li>
          <li><strong className="text-gray-900 dark:text-white">Rename or delete:</strong> Right-click a folder to rename or delete it. Conversations in a deleted folder are moved back to the main list.</li>
        </ul>
        <p className="text-gray-600 dark:text-gray-400 mt-4">
          Folders are perfect for organizing conversations by project, topic, or client.
        </p>
      </div>
    ),
  },
  {
    id: 'profile',
    title: 'Your Profile & Preferences',
    content: (
      <div className="space-y-4">
        <p>Customize your experience from the profile page.</p>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Accessing Your Profile</h4>
        <p className="text-gray-600 dark:text-gray-400">
          Click your avatar in the top-right corner and select <strong className="text-gray-900 dark:text-white">Profile</strong> from the dropdown menu.
        </p>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mt-4">Available Settings</h4>
        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
          <li><strong className="text-gray-900 dark:text-white">Name & Organization:</strong> Update your display name and organization.</li>
          <li><strong className="text-gray-900 dark:text-white">Default Model:</strong> Set which AI model is selected by default when starting new conversations.</li>
          <li><strong className="text-gray-900 dark:text-white">Theme:</strong> Toggle between light and dark mode.</li>
          <li><strong className="text-gray-900 dark:text-white">Context Management:</strong> Enable automatic context window management for long conversations.</li>
          <li><strong className="text-gray-900 dark:text-white">Data Export:</strong> Download all your conversation data as a JSON file.</li>
          <li><strong className="text-gray-900 dark:text-white">Account Deletion:</strong> Permanently delete your account and all associated data.</li>
        </ul>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mt-4">Usage Limits</h4>
        <p className="text-gray-600 dark:text-gray-400">
          Your profile also shows your current usage limits, including messages per hour and tokens per day.
          These help ensure fair access for all users.
        </p>
      </div>
    ),
  },
  {
    id: 'dark-mode',
    title: 'Dark Mode',
    content: (
      <div className="space-y-4">
        <p>The platform supports both light and dark themes.</p>
        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
          <li>Click the sun/moon icon in the navigation bar to toggle between themes.</li>
          <li>You can also set your preferred theme in your profile preferences.</li>
          <li>The platform respects your system's theme preference on first visit.</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'keyboard-shortcuts',
    title: 'Keyboard Shortcuts',
    content: (
      <div className="space-y-4">
        <p>Speed up your workflow with these keyboard shortcuts:</p>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-2 pr-4 font-semibold text-gray-900 dark:text-white">Shortcut</th>
                <th className="text-left py-2 font-semibold text-gray-900 dark:text-white">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {[
                ['Enter', 'Send message'],
                ['Shift + Enter', 'New line in message'],
                ['Ctrl / Cmd + N', 'New conversation'],
                ['Ctrl / Cmd + K', 'Search conversations'],
              ].map(([key, action]) => (
                <tr key={key}>
                  <td className="py-2 pr-4">
                    <kbd className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 font-mono">
                      {key}
                    </kbd>
                  </td>
                  <td className="py-2 text-gray-600 dark:text-gray-400">{action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
  },
  {
    id: 'security',
    title: 'Security & Privacy',
    content: (
      <div className="space-y-4">
        <p>Your data security is our top priority.</p>
        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
          <li><strong className="text-gray-900 dark:text-white">Encryption:</strong> All sensitive data is encrypted at rest using AES-256 encryption.</li>
          <li><strong className="text-gray-900 dark:text-white">Passwords:</strong> Passwords are hashed using bcrypt and are never stored in plain text.</li>
          <li><strong className="text-gray-900 dark:text-white">Sessions:</strong> Sessions are secured with httpOnly cookies and expire after 4 hours of inactivity.</li>
          <li><strong className="text-gray-900 dark:text-white">Self-Hosted:</strong> The platform can be self-hosted, so data never leaves your organization's infrastructure.</li>
          <li><strong className="text-gray-900 dark:text-white">Audit Logging:</strong> All user actions are logged with cryptographic signatures for compliance.</li>
          <li><strong className="text-gray-900 dark:text-white">Data Control:</strong> You can export or delete all your data at any time from your profile.</li>
        </ul>
        <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-sm text-green-800 dark:text-green-300">
            <strong>Privacy:</strong> Your conversations are only visible to you and your account administrators.
            We do not use your data to train AI models.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'faq',
    title: 'Frequently Asked Questions',
    content: (
      <div className="space-y-6">
        {[
          {
            q: 'Which AI model should I use?',
            a: 'It depends on your task. GPT-4 and Claude are excellent for general-purpose tasks and long-form writing. Gemini is strong with factual queries. DeepSeek Coder excels at programming tasks. Use the model comparison feature to find the best fit for your needs.',
          },
          {
            q: 'Is there a message limit?',
            a: 'Yes, usage limits depend on your plan. You can view your current limits and usage on your profile page. Limits typically include messages per hour and tokens per day.',
          },
          {
            q: 'What happens to my data if I delete my account?',
            a: 'All your conversations, files, and personal data are permanently deleted. This action cannot be undone. You may want to export your data first from the profile page.',
          },
          {
            q: 'Why am I getting rate-limited?',
            a: 'Rate limits ensure fair access for all users. If you\'re seeing rate limit messages, wait a few minutes before sending more messages. You can see your remaining quota on your profile.',
          },
          {
            q: 'Can I access the platform on mobile?',
            a: 'Yes, the platform is fully responsive and works on mobile browsers. Simply visit the same URL on your phone or tablet.',
          },
        ].map(({ q, a }) => (
          <div key={q}>
            <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">{q}</h4>
            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">{a}</p>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    content: (
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Common Issues</h4>
        <div className="space-y-4">
          {[
            {
              issue: 'I can\'t sign in',
              fix: 'Make sure your email is verified. Try the "Forgot Password" link to reset your password. Clear your browser cookies and try again.',
            },
            {
              issue: 'Messages aren\'t sending',
              fix: 'Check your internet connection. If the issue persists, the backend service may be temporarily unavailable. Try refreshing the page.',
            },
            {
              issue: 'Responses are cut off',
              fix: 'Long responses may hit the model\'s token limit. Try breaking your prompt into smaller, more specific questions.',
            },
            {
              issue: 'File upload fails',
              fix: 'Ensure your file is under the size limit and in a supported format (PDF, TXT, DOCX, CSV, code files). Check your file quota on the profile page.',
            },
            {
              issue: 'The page looks broken',
              fix: 'Try clearing your browser cache and refreshing. If using a very old browser, please update to the latest version of Chrome, Firefox, Safari, or Edge.',
            },
          ].map(({ issue, fix }) => (
            <div key={issue} className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <p className="font-medium text-gray-900 dark:text-white mb-1">{issue}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{fix}</p>
            </div>
          ))}
        </div>
        <p className="text-gray-600 dark:text-gray-400 mt-4">
          If you're still experiencing issues, please reach out to our support team for assistance.
        </p>
      </div>
    ),
  },
];

export default function DocsPage() {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('getting-started');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Handle hash navigation
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setActiveSection(id);
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location.hash]);

  // Intersection observer for scroll-based active section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -60% 0px' }
    );

    sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-3">
                <img src={logoImg} alt="Logo" className="w-8 h-8" />
                <span className="text-lg font-semibold text-gray-900 dark:text-white">Unified LLM</span>
              </Link>
              <span className="text-gray-300 dark:text-gray-600">/</span>
              <span className="text-sm text-gray-600 dark:text-gray-400">Documentation</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                Home
              </Link>
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
                  </svg>
                )}
              </button>
              {/* Mobile nav toggle */}
              <button
                onClick={() => setMobileNavOpen(!mobileNavOpen)}
                className="lg:hidden p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-12">
          {/* Sidebar */}
          <aside className={`${mobileNavOpen ? 'block' : 'hidden'} lg:block`}>
            <div className="lg:sticky lg:top-24 space-y-1">
              <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium mb-3 px-3">
                Contents
              </p>
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  onClick={() => setMobileNavOpen(false)}
                  className={`block px-3 py-2 text-sm rounded-lg transition-colors ${
                    activeSection === section.id
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {section.title}
                </a>
              ))}
            </div>
          </aside>

          {/* Content */}
          <main className="min-w-0">
            <div className="mb-12">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
                Documentation
              </h1>
              <p className="mt-3 text-lg text-gray-600 dark:text-gray-400">
                Everything you need to know about using the Unified Multi-LLM Platform.
              </p>
            </div>

            <div className="space-y-16">
              {sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-24">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 pb-3 border-b border-gray-200 dark:border-gray-800">
                    {section.title}
                  </h3>
                  <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {section.content}
                  </div>
                </section>
              ))}
            </div>

            {/* Back to top */}
            <div className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-800 text-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                Back to top
              </button>
            </div>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            &copy; 2026 IH Software Services Provider. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              Home
            </Link>
            <Link to="/docs" className="text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              Docs
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
